import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Avatar from "../components/Avatar";
import { useCommunityAuth } from "./CommunityAuthContext";
import "./AccountMenu.css";

const CLOSE_DELAY = 180; // ms, so the menu doesn't vanish while moving the mouse onto it

// The header passes translated labels on the storefront; everywhere else, these apply
const DEFAULT_LABELS = {
  account: "Account",
  logOut: "Log out",
  myProfile: "My profile",
  signIn: "Sign in",
};

/**
 * Header account control.
 *  - Visitors (anonymous): a "Sign in" button that opens the AuthModal.
 *  - Members: their avatar. Hover (on devices with a mouse) or click/tap opens a
 *    small menu with "My profile" and "Log out". "Log out" opens SignOutModal.
 *
 * `profile` is the member's Firestore user doc (for name/photo), e.g. from useUser(currentUid).
 */
const AccountMenu = ({ profile, labels = DEFAULT_LABELS }) => {
  const { user, isSignedIn, openAuth, openSignOut } = useCommunityAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const buttonRef = useRef(null);
  const closeTimer = useRef(null);
  const menuId = useId();
  const canHover = typeof window !== 'undefined' && window.matchMedia?.('(hover: hover)').matches;

  // Close on outside click and on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  if (!isSignedIn) {
    return (
      <button type="button" className="account-signin" onClick={() => openAuth({ mode: "signin" })}>
        {labels.signIn}
      </button>
    );
  };

  const display = profile || { name: user.displayName || "Me", photoUrl: user.photoURL || "" };
  const firstName = (display.name || "Me").split(" ")[0];

  const hoverOpen = () => {
    if (!canHover) return;
    clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const hoverClose = () => {
    if (!canHover) return;
    closeTimer.current = setTimeout(() => setOpen(false), CLOSE_DELAY);
  };

  const onButtonKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => wrapRef.current?.querySelector("[role=\"menuitem\"]")?.focus());
    };
  };

  const onMenuKeyDown = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    e.preventDefault();
    const items = [...wrapRef.current.querySelectorAll("[role=\"menuitem\"]")];
    const i = items.indexOf(document.activeElement);
    const next = e.key === "ArrowDown"
      ? (i + 1) % items.length
      : (i - 1 + items.length) % items.length;
    items[next].focus();
  };

  return (
    <div className="account" ref={wrapRef} onMouseEnter={hoverOpen} onMouseLeave={hoverClose}>
      <button
        type="button"
        className="account__trigger"
        ref={buttonRef}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        // With a mouse, hovering has already opened it, so a click must not toggle it shut.
        // Touch screens: tap toggles. Moving away, clicking outside or Escape closes it.
        onClick={() => setOpen((o) => (canHover ? true : !o))}
        onKeyDown={onButtonKeyDown}
      >
        <Avatar user={display} size={36} />
        <span className="account__name">{firstName}</span>
        <svg
          className="account__chevron"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          className="account__menu"
          id={menuId}
          role="menu"
          aria-label={labels.account}
          onKeyDown={onMenuKeyDown}
        >
          <div className="account__who" role="presentation">
            <b>{display.name}</b>
            {user.email && <span>{user.email}</span>}
          </div>
          <Link
            role="menuitem"
            className="account__item"
            to={`/profile/${user.uid}`}
            onClick={() => setOpen(false)}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="8" r="4" />
              <path d="M4 21c.8-4 4-6.5 8-6.5s7.2 2.5 8 6.5" />
            </svg>
            {labels.myProfile}
          </Link>
          <button
            type="button"
            role="menuitem"
            className="account__item"
            onClick={() => {
              setOpen(false);
              openSignOut();
            }}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            {labels.logOut}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountMenu;
